import { getFocusableElements } from '@theme/a11y';
import { events } from '@theme/event-bus';
import { classes, getAttributeValue, isFromBreakpoint, isUntilBreakpoint, throttle } from '@theme/helpers';
import { HTMLCustomElement } from '@theme/html-custom-element';

class SiteHeader extends HTMLCustomElement {
  connectedCallback() {
    new ResizeObserver(() => {
      const { height, x } = this.refs.masthead.getBoundingClientRect();

      /**
       * Set the height to be the element height + padding.
       */
      document.body.style.setProperty('--header-height', `${(height + x * 2).toFixed(2)}px`);
    }).observe(this);

    this.on('click', this.refs.menuToggle, () => {
      if (this.classList.contains(classes.open)) {
        this.close();
        return;
      }

      this.open();
    });

    this.subscribe(events.escapeKey, this.close.bind(this));

    this.on(
      'resize',
      window,
      throttle(() => {
        if (isFromBreakpoint('medium')) {
          this.classList.remove(classes.open);
        }
      }),
    );
  }

  /**
   * Opens the mobile menu.
   */
  open() {
    this.classList.add(classes.open);

    if (this.refs.mobileMenu) {
      for (const focusableElement of getFocusableElements(this.refs.mobileMenu)) {
        focusableElement.tabIndex = 0;
      }
    }

    /**
     * Lock the body scroll.
     */
    document.body.style.overflow = 'hidden';
  }

  /**
   * Closes the mobile menu.
   */
  close() {
    this.classList.remove(classes.open);

    if (this.refs.mobileMenu) {
      for (const focusableElement of getFocusableElements(this.refs.mobileMenu)) {
        focusableElement.tabIndex = -1;
      }
    }

    /**
     * Release the body scroll.
     */
    document.body.style.overflow = '';
  }
}

class HeaderMenu extends HTMLCustomElement {
  connectedCallback() {
    if (super.connectedCallback) super.connectedCallback();

    this.items = this.refs.item ? this.refs.item.all : [];
    this.dropdowns = this.refs.dropdown ? this.refs.dropdown.all : [];

    this.on('mouseleave', [...this.items, ...this.dropdowns], (event) => {
      if (
        !(
          isUntilBreakpoint('medium') ||
          (this.refs.dropdown && this.refs.dropdown.parentElement.contains(event.relatedTarget))
        )
      ) {
        this.displayItem(event.currentTarget.dataset.block, 'close');
      }
    });

    if (this.refs.item) {
      this.on('mouseleave', this.items, () => {
        if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
          this.moveHighlight();
        }
      });

      this.on('mouseenter', this.items, (event) => {
        if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
          this.moveHighlight();
        }

        if (isFromBreakpoint('medium')) {
          this.displayItem(event.currentTarget.dataset.block, 'open');
        }
      });

      this.on('click', this.items, (event) => {
        if (isUntilBreakpoint('medium') && !!this.getDropdown(event.currentTarget.dataset.block)) {
          /**
           * Prevent the target link.
           */
          event.preventDefault();

          this.displayItem(event.currentTarget.dataset.block, 'open');
        }
      });
    }

    if (
      getAttributeValue(this, 'open-first-item', false) &&
      this.refs.item &&
      !this.items.find((item) => item !== this.refs.item && item.classList.contains(classes.active))
    ) {
      if (this.getDropdown(this.refs.item.dataset.block)) {
        this.displayItem(this.refs.item.dataset.block, 'open');
      }
    }

    for (const focusableElement of this.dropdowns.map(getFocusableElements).flat()) {
      focusableElement.tabIndex = -1;
    }

    this.on(
      'resize',
      window,
      throttle(() => {
        this.moveHighlight();
      }),
      null,
      true,
    );

    setTimeout(() => {
      this.refs.highlight.classList.add(classes.ready);
    }, 250);
  }

  /**
   * Moves and resizes the custom menu item highlight element.
   */
  moveHighlight() {
    const openItem = this.items.find(({ classList }) => classList.contains(classes.open));
    const activeItem = this.items.find(({ classList }) => classList.contains(classes.active));
    const hoverItem = this.items.find((element) => element.matches(':hover'));
    const item = hoverItem ?? openItem ?? activeItem;

    if (item && item.offsetWidth) {
      const childRect = item.getBoundingClientRect();
      const parentRect = item.parentElement.getBoundingClientRect();

      this.refs.highlight.style.width = `${item.getBoundingClientRect().width}px`;
      this.refs.highlight.style.left = `${childRect.left - parentRect.left}px`;

      /**
       * Set the highlight active state once resized and moved.
       */
      this.refs.highlight.classList.add(classes.active);
      return;
    }

    this.refs.highlight.classList.remove(classes.active);
  }

  /**
   * Returns a dropdown by it's menu item.
   *
   * @param {string} blockId
   */
  getDropdown(blockId) {
    return this.dropdowns.find((element) => element.dataset.block === blockId);
  }

  /**
   * Hide or show the menu item dropdown.
   *
   * @param {string} blockId
   * @param {'toggle'|'open'|'close'} action
   */
  displayItem(blockId, action = 'toggle') {
    const methodMap = {
      open: 'add',
      close: 'remove',
      toggle: 'toggle',
    };

    const elements = [...this.items, ...this.dropdowns];
    const method = methodMap[action];

    for (const element of elements) {
      if (blockId === element.dataset.block) {
        element.classList[method](classes.open);
      } else {
        element.classList.remove(classes.open);
      }
    }

    for (const element of this.dropdowns) {
      for (const focusableElement of getFocusableElements(element)) {
        focusableElement.tabIndex =
          element.dataset.block === blockId && element.classList.contains(classes.open) ? 0 : -1;
      }
    }

    this.moveHighlight();
  }
}

if (!customElements.get('header-menu')) customElements.define('header-menu', HeaderMenu);
if (!customElements.get('site-header')) customElements.define('site-header', SiteHeader);
